<script>
    /* eslint-disable camelcase */
    import { mapGetters } from 'vuex';
    import TitleBar from '@/components/TitleBar.vue';
    import CloseIcon from '@/components/Icons/CloseIcon.vue';
    import OutlineModalItem from '@/views/ConductInspection/Modals/OutlineModalItem.vue';
    import { INSPECTION_ITEM_RESPONSE, INSPECTION_ITEM_TYPE } from '@/constants';

    const filterHeaderWithNormalQuestions = (child) => !(
        child.params
        && child.params.header
        && !child.items.find((item) => (item.params && !item.params.automatic))
    );

    const filterChildrenItemsWithoutQuestions = ({ type, allConditionsMet, repeat }) => (
        type !== INSPECTION_ITEM_TYPE.QUESTION
        && !(type === INSPECTION_ITEM_TYPE.CONDITIONAL && !allConditionsMet)
        && !repeat
    );

    const flattenResponses = (acc, { children, uuid, ...rest }) => ({
        ...acc,
        [uuid]: rest,
        ...children.reduce(flattenResponses, {}),
    });

    const calculateResponses = (form, validationErrors = {}) => {
        const findAndCountFirstLevelQuestions = (child) => (child.items ? child.items
            .filter(({ type, params, response_type }) => {
                const isAutomaticQuestion = params && params.automatic;
                const isInstructionQuestion = response_type && response_type === INSPECTION_ITEM_RESPONSE.INSTRUCTION;
                return type === INSPECTION_ITEM_TYPE.QUESTION && !isAutomaticQuestion && !isInstructionQuestion;
            })
            .reduce((acc, value) => ({
                questions: acc.questions + 1,
                responses: (value.responded && !(value.uuid in validationErrors)) ? acc.responses + 1 : acc.responses,
                requiredChildren: acc.requiredChildren || value.required,
            }), {
                questions: 0,
                responses: 0,
                requiredChildren: false,
            }) : []);

        const deepQuestionsCount = (item) => {
            const childrenQuestions = findAndCountFirstLevelQuestions(item);
            const childrenItemsWithoutQuestions = item.items ? item.items
                .filter(filterChildrenItemsWithoutQuestions)
                .map(deepQuestionsCount) : [];

            const counts = childrenItemsWithoutQuestions.reduce((acc, value) => ({
                questions: acc.questions + value.questions,
                responses: acc.responses + value.responses,
                requiredChildren: acc.requiredChildren || value.requiredChildren,
            }), {
                questions: childrenQuestions.questions,
                responses: childrenQuestions.responses,
                requiredChildren: childrenQuestions.requiredChildren,
            });

            return {
                uuid: item.uuid,
                questions: counts.questions,
                responses: counts.responses,
                requiredChildren: counts.requiredChildren || false,
                children: childrenItemsWithoutQuestions,
            };
        };

        const { children, uuid, ...rest } = deepQuestionsCount(form);
        return children
            .filter(filterHeaderWithNormalQuestions)
            .reduce(flattenResponses, {
                [uuid]: rest,
            });
    };

    export default {
        name: 'OutlineModal',
        components: { CloseIcon, OutlineModalItem, TitleBar },
        props: {
            checklist: {
                type: Object,
                required: true,
            },
        },
        methods: {
            modalClosed() {
                return this.$emit('close');
            },
        },
        computed: {
            ...mapGetters({
                validationErrors: 'conduct/errors',
            }),
            responsesStatistics() {
                return calculateResponses(this.checklist.form, this.validationErrors);
            },
            formItems() {
                if (!this.checklist.form.items) return [];
                return this.checklist.form.items
                    .filter(filterHeaderWithNormalQuestions);
            },
            isOnlyOnePage() {
                return this.formItems.length <= 1;
            },
        },
    };
</script>

<template>
    <modal @modal-close="modalClosed" full-width>
        <TitleBar
            :title="$t('Outline')"
            @left-click="modalClosed"
            fixed
        >
            <template v-slot:leftIcon>
                <CloseIcon />
            </template>
        </TitleBar>

        <div class="modal-content" style="padding-top: 4.5rem;">
            <div class="flex flex-col" v-if="formItems">
                <OutlineModalItem
                    class="flex flex-col justify-between"
                    v-for="page in formItems"
                    :key="page.uuid"
                    :responses-statistics="responsesStatistics"
                    :is-only-one-page="isOnlyOnePage"
                    :item="page"
                    :page="page.uuid"
                />
            </div>
        </div>
    </modal>
</template>
